import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle, Key } from '@phosphor-icons/react';
import { authAPI } from '../lib/api';

/**
 * Public claim page reached via /claim-account?token=...
 *
 * Flow:
 *   1. Mount → call GET /api/claim-account/validate?token=... to confirm the
 *      token is still good. If not, display the precise error from the
 *      backend (expired, used, invalid) and stop.
 *   2. Show the player's name + login email so they know which account
 *      they're claiming.
 *   3. They set + confirm a password. POST to /api/claim-account/complete.
 *   4. On success, push them to /login with a toast that says
 *      "Your account is ready. Please log in." (no auto-login — keeps the
 *      flow simple and the token redemption auditable.)
 */
export default function ClaimAccountPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [validating, setValidating] = useState(true);
  const [validationError, setValidationError] = useState(null);
  const [account, setAccount] = useState(null);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidationError('Missing claim token.');
      setValidating(false);
      return;
    }
    let mounted = true;
    authAPI.validateClaimToken(token)
      .then((r) => {
        if (mounted) setAccount(r.data);
      })
      .catch((err) => {
        if (mounted) setValidationError(err?.response?.data?.detail || 'This claim link is invalid.');
      })
      .finally(() => {
        if (mounted) setValidating(false);
      });
    return () => { mounted = false; };
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await authAPI.completeClaim({ token, password, confirm_password: confirm });
      toast.success('Your account is ready. Please log in.');
      navigate('/login');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not complete claim.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4ede0] flex items-center justify-center p-4" data-testid="claim-account-page">
      <div className="w-full max-w-md bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-6 space-y-5">
        <div className="space-y-1 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#015e3f]/10 text-[#015e3f] mb-2">
            <Key size={22} weight="bold" />
          </div>
          <h1 className="font-serif text-2xl text-[#013523]">Claim your account</h1>
          <p className="text-xs text-[#6a766e]">The Rippers Club</p>
        </div>

        {validating && (
          <div className="text-center text-sm text-[#6a766e]" data-testid="claim-validating">
            Checking your claim link…
          </div>
        )}

        {!validating && validationError && (
          <div
            className="bg-[#fdecec] border border-[#f1c8c8] text-[#7a2a2a] rounded-lg p-4 text-sm"
            data-testid="claim-error"
          >
            {validationError}
            <div className="mt-2 text-xs text-[#7a2a2a]/80">
              Ask the club admin for a new claim link.
            </div>
          </div>
        )}

        {!validating && account && (
          <form onSubmit={submit} className="space-y-4" data-testid="claim-form">
            <div className="bg-[#f0ece3]/50 border border-[#e3dcd1] rounded-lg p-3 text-sm space-y-1">
              <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Account</div>
              <div className="text-[#013523] font-medium" data-testid="claim-name">{account.name}</div>
              <div className="text-xs text-[#6a766e]">Login email: <span className="text-[#013523]" data-testid="claim-email">{account.email}</span></div>
            </div>

            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-[#6a766e] mb-1">Set password *</span>
              <input
                data-testid="claim-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="w-full px-3 py-2 rounded-lg border border-[#e3dcd1] bg-white text-sm"
              />
              <span className="block text-[11px] text-[#6a766e] mt-1">At least 8 characters.</span>
            </label>

            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-[#6a766e] mb-1">Confirm password *</span>
              <input
                data-testid="claim-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="w-full px-3 py-2 rounded-lg border border-[#e3dcd1] bg-white text-sm"
              />
            </label>

            <button
              type="submit"
              disabled={busy}
              data-testid="claim-submit"
              className="w-full px-4 py-2.5 rounded-lg bg-[#015e3f] text-white font-medium hover:bg-[#013523] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <CheckCircle size={16} weight="bold" />
              {busy ? 'Activating…' : 'Set password & activate'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
